import React, { useEffect, ReactNode, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAppDispatch, useAppSelector } from '~/shared/hooks/redux';
import { checkAuthorization } from '~/rtk-store/auth';
import { useTranslation } from 'next-i18next';

import 'swiper/css';
import 'swiper/css/effect-fade';

import styles from './style.module.scss';

// ============================================================================
// Types
// ============================================================================

interface AuthLayoutProps {
    children: ReactNode;
}

// ============================================================================
// Constants
// ============================================================================

const SLIDER_IMAGES = ['1', '4', '6', '8', '10', '12', '13', '14', '16', '18'];

// ============================================================================
// Helpers
// ============================================================================

function shuffleArray<T>(arr: T[]): T[] {
    return [...arr].sort(() => Math.random() - 0.5);
}

// ============================================================================
// Component
// ============================================================================

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common');
    const themeMode = useAppSelector((state) => state?.theme?.mode);

    // Shuffle images once on mount
    const shuffledImages = useMemo(() => shuffleArray(SLIDER_IMAGES), []);

    // Check authorization on mount
    useEffect(() => {
        dispatch(checkAuthorization());
    }, [dispatch]);

    // Determine if we're on register page
    const isRegisterPage = router.pathname.startsWith('/auth/register');

    return (
        <div className={styles.container}>
            {/* Left Side - Image Slider */}
            <aside className={styles.leftSide}>
                <div className={styles.leftHeader}>
                    <Link href="/">
                        <img
                            src={themeMode === 'dark' ? '/static/img/soff-logos/soff_logo_white.webp' : '/static/img/seller-logo.jpg'}
                            alt="Soff.uz"
                            width={140}
                            className={styles.leftLogo}
                        />
                    </Link>
                </div>

                <div className={styles.swiperWrapper}>
                    <Swiper
                        className={styles.swiper}
                        modules={[EffectFade, Autoplay]}
                        effect="fade"
                        loop
                        autoplay={{
                            delay: 2500,
                            disableOnInteraction: false,
                        }}
                        speed={1500}
                        pagination={false}
                        navigation={false}
                    >
                        {shuffledImages.map((imageId) => (
                            <SwiperSlide key={imageId} className={styles.swiperSlide}>
                                <img
                                    src={`/static/img/auth/img${imageId}.png`}
                                    alt="Soff.uz illustration"
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </aside>

            {/* Right Side - Form */}
            <main className={styles.rightSide}>
                <header className={styles.rightHeader}>
                    {/* Mobile Logo */}
                    <Link href="/">
                        <img
                            src={themeMode === 'dark' ? '/static/img/soff-logos/soff_logo_white.webp' : '/static/img/seller-logo.jpg'}
                            alt="Soff.uz"
                            width={100}
                            className={styles.mobileLogo}
                        />
                    </Link>

                    {/* Desktop - Hidden logo placeholder for spacing */}
                    <div className={styles.desktopLogo} />

                    {/* Auth Link */}
                    {isRegisterPage ? (
                        <Link href="/auth/login">
                            <a className={styles.headerLink}>{t('auth.layout.login_link')}</a>
                        </Link>
                    ) : (
                        <Link href="/auth/register">
                            <a className={styles.headerLink}>{t('auth.layout.register_link')}</a>
                        </Link>
                    )}
                </header>

                <div className={styles.formContainer}>
                    <div className={styles.formWrapper}>
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AuthLayout;
